import { isFunction, isNaN, isNil, map, sortBy, toNumber } from 'lodash';

import Notifications from '../../../../../common/utils/Notifications';
import { defaultErrorMessage } from '../../../../errors';
import { getNodeId } from '../store/actionHandlers/common/adapterActions';

import updateTreeState from '../store/actionHandlers/common/updateTreeState';
import { finishExpandAnimation } from '../store/actions';
import { BEGIN_LAZY_LOADING, UPDATE_LAZY_LOADING } from '../store/constants';

export default class LazyLoading {
  constructor({
    nodeCanLoadChildren = true,
    reloadSeedData = true,
    createObservableQuery = () => {
      throw new Error(
        'createObservableQuery should be provided to LazyLoading',
      );
    },
    cookModels = (parentNode, models) => models,
    t, // = text => text,
  }) {
    this._reloadSeedData = reloadSeedData;
    this._nodeCanLoadChildren = nodeCanLoadChildren;
    this._createObservableQuery = createObservableQuery;
    this._cookModels = cookModels;
    this._t = t;
    this.items = [];
    this.dispatched = [];
  }

  get key() {
    return 'lazyLoading';
  }

  nodeCanLoadChildren(nodeId, models) {
    if (models[nodeId] && !!models[nodeId].hasChildren) {
      return true;
    } else if (models[nodeId] && models[nodeId].hasChildren === false) {
      return false;
    }

    if (isFunction(this._nodeCanLoadChildren)) {
      return !!this._nodeCanLoadChildren((nodeId && models[nodeId]) || null);
    }
    return !!this._nodeCanLoadChildren;
  }

  prepareTreeState(treeState) {
    treeState.observedQueries = {};
  }

  tearDownTreeState(treeState) {
    Object.values(treeState.observedQueries)
      .filter(value => value) // prevent reading deleted queries
      .forEach(({ subscription }) => subscription.unsubscribe());
    treeState.observedQueries = {};
  }

  handleDidAddModels({ models, children }, addedModels, { adapter }) {
    for (const model of addedModels) {
      const nodeId = getNodeId(adapter, model);
      if (this.nodeCanLoadChildren(nodeId, models) && !children[nodeId]) {
        children[nodeId] = null;
      }
    }
  }

  handleDidRemoveModels(treeState, removedIds) {
    const idsToCleanUp = new Set();

    removedIds.forEach(id => {
      if (treeState.observedQueries[id]) {
        idsToCleanUp.add(id);
      }
    });

    if (idsToCleanUp.size) {
      const observedQueries = { ...treeState.observedQueries };

      idsToCleanUp.forEach(id => {
        observedQueries[id].subscription.unsubscribe();
        delete observedQueries[id];
      });

      treeState.observedQueries = observedQueries;
    }
  }

  reloadNode(treeState, nodeId) {
    const observedQueries = { ...treeState.observedQueries };
    const observedQuery = observedQueries[nodeId];

    if (observedQuery) {
      observedQuery.subscription.unsubscribe();
    }

    delete observedQueries[nodeId];
    treeState.observedQueries = observedQueries;

    this.dispatched = this.dispatched.filter(id => id !== nodeId);
  }

  shouldInitiateLoading({ children, models, observedQueries }, nodeId) {
    return (
      !observedQueries[nodeId] &&
      this.nodeCanLoadChildren(nodeId, models) &&
      (!children[nodeId] || !children[nodeId].length || this._reloadSeedData)
    );
  }

  initiateLoading(
    treeState,
    nodeId,
    dispatch,
    { adapter, plugins, model, forceReload },
  ) {
    const { treeId, models } = treeState;

    const query = this._createObservableQuery(model || models[nodeId], {
      forceReload,
    });

    const handleError = () => {
      const t = this._t;

      dispatch({
        type: UPDATE_LAZY_LOADING,
        treeId,
        nodeId,
        models: [],
        hasErrors: true,
        adapter,
        plugins,
      });

      if ('expansion' in plugins) {
        dispatch(
          finishExpandAnimation(treeId, nodeId, false, { adapter, plugins }),
        );
      }

      Notifications.error(t('Error'), t(defaultErrorMessage), t);
    };

    const subscription = query.subscribe({
      next: result => {
        if (result.errors) {
          handleError();
        } else {
          const output = this.resolveQueryResult(result, nodeId);
          if (!this.dispatched.includes(nodeId)) {
            this.dispatched.push(nodeId);
            this.items = output;
            dispatch({
              type: UPDATE_LAZY_LOADING,
              treeId,
              nodeId,
              models: output,
              adapter,
              plugins,
            });
          }
        }
      },
      error: handleError,
    });

    dispatch({
      type: BEGIN_LAZY_LOADING,
      treeId,
      nodeId,
      query,
      subscription,
      adapter,
      plugins,
    });
  }

  handleBeginLazyLoading(
    treeState,
    { nodeId, query, subscription, adapter, plugins },
  ) {
    const nextTreeState = {
      ...treeState,
      observedQueries: {
        ...treeState.observedQueries,
        [nodeId]: { query, subscription },
      },
    };

    if (this.isLoading(nextTreeState, nodeId)) {
      return nextTreeState;
    }

    return this.handleUpdateLazyLoading(nextTreeState, {
      nodeId,
      models: this.resolveQueryResult(query.currentResult(), nodeId),
      adapter,
      plugins,
    });
  }

  handleUpdateLazyLoading(
    treeState,
    { nodeId, models, hasErrors, adapter, plugins },
  ) {
    if (hasErrors) {
      treeState.observedQueries = {
        ...treeState.observedQueries,
        [nodeId]: undefined,
      };

      return treeState;
    }

    const { nodeSorting } = plugins;
    if (nodeSorting) {
      models = nodeSorting.sortModels(models);
    }

    return reorderChildModels(
      updateTreeState(
        treeState,
        this._cookModels(treeState.models[nodeId], models),
        { adapter, plugins },
        { parentId: nodeId },
      ),
      nodeId,
      models,
      { adapter },
    );
  }

  isLoading({ observedQueries }, nodeId) {
    const query = observedQueries[nodeId];
    if (query) {
      return query.query.currentResult().loading;
    }
    return false;
  }

  collectLazyLoadedModels({ models: treeModels, observedQueries }) {
    return Object.keys(observedQueries)
      .filter(id => observedQueries[id])
      .reduce(
        (models, id) =>
          models.concat(
            this._cookModels(
              treeModels[id],
              this.resolveQueryResult(
                observedQueries[id].query.currentResult(),
                id,
              ),
            ),
          ),
        [],
      );
  }

  resolveQueryResult(queryResult, initiatedNodeId) {
    const __initiatedNodeId =
      isNil(initiatedNodeId) || isNaN(toNumber(initiatedNodeId))
        ? initiatedNodeId
        : toNumber(initiatedNodeId);
    for (const key of Object.keys(queryResult.data)) {
      const value = queryResult.data[key];
      if (Array.isArray(value)) {
        return map(value, v => ({ ...v, __initiatedNodeId }));
      }
    }

    return [];
  }

  loadExpandedNodes(treeState, models, dispatch, { adapter, plugins }) {
    const { expansion } = plugins;

    if (this.shouldInitiateLoading(treeState, null)) {
      this.initiateLoading(treeState, null, dispatch, {
        adapter,
        plugins,
      });
    } else if (expansion && models) {
      for (const model of models) {
        const id = getNodeId(adapter, model);
        if (
          expansion.nodeIsExpanded(treeState, id) &&
          this.shouldInitiateLoading(treeState, id)
        ) {
          this.initiateLoading(treeState, id, dispatch, {
            adapter,
            plugins,
          });
        }
      }
    }
  }
}

function reorderChildModels(treeState, parentId, orderedModels, { adapter }) {
  const childIds = treeState.children[parentId];
  if (!childIds || !childIds.length) {
    return treeState;
  }

  const ordering = orderedModels.reduce((map, node, nodeIndex) => {
    map.set(getNodeId(adapter, node), nodeIndex);
    return map;
  }, new Map());

  treeState.children = {
    ...treeState.children,
    [parentId]: sortBy(treeState.children[parentId], [
      id => ordering.get(id) || -1,
    ]),
  };

  return treeState;
}
